/**
 * Session Cache
 * In-memory TTL cache with single-flight deduplication for Excel add-in runtime
 */

export type SessionCacheResult<T> = {
  value: T;
  outcome: "hit" | "miss";
  joinCount?: number;
};

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

interface InFlightEntry<T> {
  promise: Promise<T>;
  joinCount: number;
}

/**
 * Make cache key with time-based bucket
 * @param contractSymbol - Canonical contract symbol
 * @param bucketMs - Bucket size in milliseconds
 * @param nowMs - Optional current time in milliseconds (for testing)
 * @returns Cache key string: "contractSymbol|bucket"
 */
export function makeAsOfBucketKey(
  contractSymbol: string,
  bucketMs: number,
  nowMs?: number
): string {
  const now = nowMs !== undefined ? nowMs : Date.now();
  const bucket = Math.floor(now / bucketMs);
  return contractSymbol + "|" + bucket;
}

/**
 * Generic in-memory TTL cache with single-flight deduplication
 * Safe for Excel custom functions (no DOM/async storage dependencies)
 */
export class SessionCache<T> {
  private cache = new Map<string, CacheEntry<T>>();
  private inFlight = new Map<string, InFlightEntry<T>>();
  private readonly maxEntries: number;
  private accessOrder: string[] = []; // FIFO tracking

  constructor(maxEntries: number = 500) {
    this.maxEntries = maxEntries;
  }

  /**
   * Get value from cache (returns undefined if expired or not found)
   */
  get(key: string): T | undefined {
    this.purgeExpired();
    const entry = this.cache.get(key);
    if (!entry) {
      return undefined;
    }
    if (entry.expiresAt <= Date.now()) {
      this.cache.delete(key);
      this.removeFromAccessOrder(key);
      return undefined;
    }
    // Update access order (move to end for FIFO)
    this.updateAccessOrder(key);
    return entry.value;
  }

  /**
   * Set value in cache with TTL
   */
  set(key: string, value: T, ttlMs: number): void {
    this.purgeExpired();
    const expiresAt = Date.now() + ttlMs;
    const entry: CacheEntry<T> = { value, expiresAt };

    // If key exists, update it
    if (this.cache.has(key)) {
      this.cache.set(key, entry);
      this.updateAccessOrder(key);
      return;
    }

    // Check if we need to evict
    if (this.cache.size >= this.maxEntries) {
      this.evictOldest();
    }

    this.cache.set(key, entry);
    this.updateAccessOrder(key);
  }

  /**
   * Get value from cache, or set it using factory function
   * Single-flight: concurrent calls for same key share one promise
   */
  async getOrSet(
    key: string,
    ttlMs: number,
    factory: () => Promise<T>
  ): Promise<T> {
    const result = await this.getOrSetWithOutcome(key, ttlMs, factory);
    return result.value;
  }

  /**
   * Like getOrSet but returns { value, outcome } where outcome is:
   * - "hit": value was in cache
   * - "join": in-flight request existed, caller awaited it
   * - "miss": caller executed factory
   */
  async getOrSetWithOutcome(
    key: string,
    ttlMs: number,
    factory: () => Promise<T>
  ): Promise<{ value: T; outcome: "hit" | "miss" | "join" }> {
    const cached = this.get(key);
    if (cached !== undefined) {
      return { value: cached, outcome: "hit" };
    }

    const inFlightEntry = this.inFlight.get(key);
    if (inFlightEntry) {
      inFlightEntry.joinCount++;
      const value = await inFlightEntry.promise;
      return { value, outcome: "join" };
    }

    const promise = factory()
      .then((value) => {
        this.set(key, value, ttlMs);
        return value;
      })
      .finally(() => {
        this.inFlight.delete(key);
      });

    this.inFlight.set(key, { promise, joinCount: 0 });
    const value = await promise;
    return { value, outcome: "miss" };
  }

  /**
   * Like getOrSet but returns leader stats: outcome "hit"|"miss" and joinCount on miss.
   * Factory receives getJoinCount() to read joiner count (e.g. for diagnostics).
   * Optional onJoin callback is invoked when the caller joined an in-flight request.
   * Optional shouldCache: only store value when true (e.g. skip caching error results).
   */
  async getOrSetWithLeaderStats<T>(
    key: string,
    ttlMs: number,
    factory: (getJoinCount: () => number) => Promise<T>,
    opts?: { onJoin?: () => void; shouldCache?: (value: T) => boolean }
  ): Promise<SessionCacheResult<T>> {
    const cached = this.get(key);
    if (cached !== undefined) {
      return { value: cached, outcome: "hit" };
    }

    const existing = this.inFlight.get(key) as
      | { promise: Promise<SessionCacheResult<T>>; joinCount: number }
      | undefined;
    if (existing) {
      existing.joinCount++;
      opts?.onJoin?.();
      return await existing.promise;
    }

    let resolveLeader!: (r: SessionCacheResult<T>) => void;
    let rejectLeader!: (e: unknown) => void;
    const leaderPromise = new Promise<SessionCacheResult<T>>((res, rej) => {
      resolveLeader = res;
      rejectLeader = rej;
    });

    const entry: { promise: Promise<SessionCacheResult<T>>; joinCount: number } = {
      promise: leaderPromise,
      joinCount: 0,
    };
    this.inFlight.set(key, entry);

    const getJoinCount = (): number => entry.joinCount;

    factory(getJoinCount)
      .then((value) => {
        const joinCount = entry.joinCount;
        if (!opts?.shouldCache || opts.shouldCache(value)) {
          this.set(key, value, ttlMs);
        }
        this.inFlight.delete(key);
        resolveLeader({ value, outcome: "miss", joinCount });
      })
      .catch((err) => {
        this.inFlight.delete(key);
        rejectLeader(err);
      });

    return leaderPromise;
  }

  /**
   * Clear all entries
   */
  clear(): void {
    this.cache.clear();
    this.inFlight.clear();
    this.accessOrder = [];
  }

  /**
   * Get current cache size
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Purge expired entries (lazy cleanup)
   */
  private purgeExpired(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    for (const [key, entry] of this.cache.entries()) {
      if (entry.expiresAt <= now) {
        keysToDelete.push(key);
      }
    }

    for (const key of keysToDelete) {
      this.cache.delete(key);
      this.removeFromAccessOrder(key);
    }
  }

  /**
   * Evict oldest entry (FIFO)
   */
  private evictOldest(): void {
    if (this.accessOrder.length === 0) {
      // Fallback: evict first entry in map
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
      return;
    }

    // Evict the first entry in access order (oldest)
    const oldestKey = this.accessOrder[0];
    if (this.cache.has(oldestKey)) {
      this.cache.delete(oldestKey);
    }
    this.removeFromAccessOrder(oldestKey);
  }

  /**
   * Update access order (move key to end)
   */
  private updateAccessOrder(key: string): void {
    this.removeFromAccessOrder(key);
    this.accessOrder.push(key);
  }

  /**
   * Remove key from access order
   */
  private removeFromAccessOrder(key: string): void {
    const index = this.accessOrder.indexOf(key);
    if (index !== -1) {
      this.accessOrder.splice(index, 1);
    }
  }
}

// Export singleton instance for option snapshots
export const optionSnapshotCache = new SessionCache<any>();
